import { useReducer } from "react";

export interface DataLoadingState<D> {
  data?: D;
  loading: boolean;
  error?: string;
  errorCode?: number;
}

export type LoadDataAction<D> =
  | { type: "loading" }
  | { type: "loaded"; data: D }
  | { type: "errored"; message: string; errorCode?: number };

/**
 * Reducer which calculates the next data loading state given some data loading action.
 * @param state - Current state
 * @param action - Action which occurred
 * @returns - The next loading state
 */
export function dataLoadingReducer<D>(state: DataLoadingState<D>, action: LoadDataAction<D>): DataLoadingState<D> {
  switch (action.type) {
    case "loading":
      return {
        ...state,
        loading: true,
        error: undefined,
        errorCode: undefined,
      };

    case "loaded":
      return {
        data: action.data,
        loading: false,
        error: undefined,
        errorCode: undefined,
      };

    case "errored":
      return {
        ...state,
        error: action.message,
        errorCode: action.errorCode,
        loading: false,
      };
  }
}

/**
 * Generate the initial loading state for given generic data type.
 * @param initialData - Initial value to use for data
 */
export function generateInitialLoadingState<D>(initialData?: D): DataLoadingState<D> {
  return {
    data: initialData,
    loading: false,
    error: undefined,
    errorCode: undefined,
  };
}

/**
 * React hook which initialises a data loading reducer for the given type.
 * @param initialData - Initial data value
 */
export function useDataLoadingReducer<D>(initialData?: D) {
  const dataReducer = (state: DataLoadingState<D>, action: LoadDataAction<D>) => dataLoadingReducer<D>(state, action);
  return useReducer(dataReducer, generateInitialLoadingState(initialData));
}
